import React from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LayoutDashboard, AlertTriangle, Users, Car, FileText, Wrench, LogOut, Menu, History } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Button } from './ui/button';
import { useAuth } from '../lib/auth';
import { BRAND, FEATURES } from '../lib/brand';

const NAV_BASE = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/admin/schadenfaelle', label: 'Schadenfälle', icon: AlertTriangle },
  { to: '/admin/kunden', label: 'Kunden', icon: Users },
  { to: '/admin/dokumente', label: 'Dokumente', icon: FileText },
  { to: '/admin/audit-logs', label: 'Audit-Logs', icon: History },
];

const NAV_TOWING = [
  { to: '/admin/ersatzfahrzeuge', label: 'Ersatzfahrzeuge', icon: Car },
];
const NAV_PANNENHILFE = [
  { to: '/admin/pannenhilfe', label: 'Pannenhilfe', icon: Wrench },
];

function getNav() {
  return [
    ...NAV_BASE,
    ...(FEATURES.towing_enabled ? NAV_TOWING : []),
    ...(FEATURES.pannenhilfe_enabled ? NAV_PANNENHILFE : []),
  ];
}

function SidebarLinks({ onClick }) {
  const NAV = getNav();
  return (
    <nav className="flex flex-col gap-1" data-testid="admin-sidebar">
      {NAV.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          onClick={onClick}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
              isActive
                ? 'bg-[hsl(var(--primary))] text-white'
                : 'text-slate-700 hover:bg-slate-100'
            }`
          }
          data-testid={`admin-nav-${n.to.split('/').pop() || 'dashboard'}-link`}
        >
          <n.icon className="h-4 w-4" />
          <span>{n.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Topbar */}
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="lg:hidden" aria-label="Menü" data-testid="admin-menu-button">
                  <Menu className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72">
                <div className="font-heading font-semibold text-[hsl(var(--primary))] mb-4">{BRAND.name} - Admin</div>
                <SidebarLinks onClick={() => setOpen(false)} />
              </SheetContent>
            </Sheet>
            <Link to="/admin" className="flex items-center gap-2">
              <img src="/brand/logo-light.png" alt="UnfallHeld" className="h-9 w-9 object-contain" />
              <div className="font-heading font-semibold text-[hsl(var(--primary))]">Admin</div>
            </Link>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-slate-500 hidden sm:inline">Angemeldet als</span>
            <span className="font-medium text-slate-800">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }} data-testid="admin-logout-button">
              <LogOut className="h-4 w-4 mr-1.5" /> Abmelden
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 lg:px-6 py-6 grid grid-cols-12 gap-6">
        <aside className="hidden lg:block col-span-3 xl:col-span-2">
          <div className="sticky top-20">
            <SidebarLinks />
          </div>
        </aside>
        <main className="col-span-12 lg:col-span-9 xl:col-span-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
